import { useState, useRef, useEffect } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { FaPaperPlane } from "react-icons/fa";
import NewsService from "../../../services/News";
import Url from "../../../url/Url";
import anonymous from "../../../assets/images/anonymous.jpg.webp";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/userSlice";

export default function CommentsModal({
  isCommentsModal,
  setIsCommentsModal,
  id,
}) {
  const [comments, setComments] = useState([]);
  const commentInputRef = useRef(null);
  const [newComment, setNewComment] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showAllComments, setShowAllComments] = useState(false);
  const y = useMotionValue(0);

  // فوکوس روی input هنگام باز شدن مودال
  useEffect(() => {
    if (isCommentsModal) {
      y.set(0);
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isCommentsModal, y]);

  useEffect(() => {
    const fetchComments = async () => {
      try {
        const res = await NewsService.getCommentsById(id);
        console.log("comments:", res?.data?.data);
        setComments(res?.data?.data || []);
      } catch (err) {
        console.log("error fetching comments:", err);
      }
    };

    if (id) {
      fetchComments();
    }
  }, [id]);

  // بسته شدن مودال هنگام فشار کلید Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && isCommentsModal) {
        setIsCommentsModal(false);
      }
    };

    if (isCommentsModal) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isCommentsModal, setIsCommentsModal]);

  // بسته شدن مودال با انیمیشن
  const handleCloseModal = () => {
    animate(y, 300, { duration: 0.3 });
    setTimeout(() => {
      setIsCommentsModal(false);
    }, 300);
  };

  // مدیریت درگ
  const handleDragEnd = (event, info) => {
    const threshold = 100;
    if (info.velocity.y > 500 || info.offset.y > threshold) {
      handleCloseModal();
    } else {
      animate(y, 0, {
        duration: 0.3,
        type: "spring",
        stiffness: 300,
        damping: 30,
      });
    }
  };

  const handleSendComment = async (commentText) => {
    setIsSubmittingComment(true);
    try {
      const commentData = {
        targetId: id,
        content: commentText,
      };
      const res = await NewsService.sendComment(commentData);
      console.log("comment sent successfully:", res);

      if (commentInputRef.current) {
        commentInputRef.current.value = "";
      }

      const updatedComments = await NewsService.getCommentsById(id);
      setComments(updatedComments?.data?.data || []);
      if (setIsCommentsModal) {
        setIsCommentsModal(false);
      }
    } catch (err) {
      console.log("error sending comment:", err);
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const timeAgo = (date) => {
    const diff = (Date.now() - new Date(date)) / 1000; // ثانیه

    if (diff < 60) return `${Math.floor(diff)} second ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minute ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hour ago`;
    return `${Math.floor(diff / 86400)} day ago`;
  };
  const user = useSelector(selectUser); 
  if (!isCommentsModal) return null;
  

  return (
    <motion.div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={handleCloseModal}
      ></div>
      <motion.div
        ref={contentRef}
        className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl"
        style={{ y }}
        drag="y"
        dragElastic={0.2}
        dragConstraints={{ top: 0, bottom: 0 }}
        onDragEnd={handleDragEnd}
        initial={{ y: 400 }}
        animate={{ y: 0 }}
        exit={{ y: 400 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="p-6">
          <div className="flex justify-center mb-3">
            <span className="w-12 h-1.5 bg-gray-300 rounded-full hover:bg-gray-400 transition-colors"></span>
          </div>

          <div dir="ltr" className="flex items-center justify-center mb-6">
            <h3 className="text-lg font-bold text-gray-900">Comments</h3>
          </div>

          <div className="space-y-4 max-h-64 overflow-auto">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <div
                  key={comment._id}
                  dir="ltr"
                  className="flex items-start space-x-3  p-4 bg-gray-50 rounded-lg"
                >
                  <img
                    alt={comment.author?.fullName || "کاربر ناشناس"}
                    src={
                      comment.author?.imageId?.path
                        ? Url + comment.author.imageId.path
                        : anonymous
                    }
                    className="w-8 h-8 rounded-full flex-shrink-0"
                  />
                  <div className="flex-1 text-left min-w-0 ">
                    <div className="flex items-center justify-start space-x-2  mb-1">
                      <p className="font-semibold text-sm text-dark">
                        {comment.author?.fullName || "کاربر ناشناس"}
                      </p>
                      <span className="text-xs text-secondary">
                        {timeAgo(comment.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-dark break-all">
                      {comment.content}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No comments have been posted yet</p>
                <p className="text-sm mt-1">Be the first to leave a comment</p>
              </div>
            )}
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex items-center space-x-3">
              <img
                src={user?.imageId?.path ? Url + user.imageId.path : anonymous}
                alt="You"
                className="w-10 h-10 rounded-full"
              />
              <div className="flex-1 relative">
                {/* <input ref={inputRef} type="text" placeholder="Write your comment..." value={newComment} onChange={(e) => setNewComment(e.target.value)} className="w-full pr-4 pl-12 py-3 bg-gray-50 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left" /> */}
                <input
                  ref={commentInputRef}
                  type="text"
                  placeholder="Write your comment"
                  disabled={isSubmittingComment}
                  dir="ltr"
                  className={`w-full pr-4 pl-12 py-3 bg-gray-50 border border-gray-200 ps-16 rounded-2xl placeholder:text-start focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-left ${
                    isSubmittingComment ? "bg-gray-100 cursor-not-allowed" : ""
                  }`}
                  onKeyPress={(e) => {
                    if (e.key === "Enter" && !isSubmittingComment) {
                      const commentText = e.target.value.trim();
                      if (commentText) {
                        handleSendComment(commentText);
                      }
                    }
                  }}
                />
                <button
                  onClick={() => {
                    const commentText = commentInputRef.current?.value ?? "";
                    if (commentText.trim() && !isSubmittingComment) {
                      handleSendComment(commentText.trim());
                    }
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-blue-600 hover:bg-blue-700 text-white w-9 h-9 rounded-xl flex items-center justify-center transition-colors"
                >
                  <FaPaperPlane className="text-white"></FaPaperPlane>
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
